import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { PageTitle, Button } from '@gsa/afp-component-library';
import Breadcrumbs from '../../../widgets/breadcrumbs';
import useHistoryState from '../../../hooks/use-history-state';
import BidSearchSection from './bid-search-section';
import BidEvaluationTable from './bid-evaluation-table';
import {
  SEARCH_OPTIONS,
  GET_BIDS_FOR_EVALUATION,
  parseSortValue,
  getKeyLabel,
} from './bid-search-helpers';

const BidSearch = () => {
  const navigate = useNavigate();

  // The last search run on this page, kept on the history entry so the
  // browser back button brings the results back (breadcrumbs start clean).
  const [lastSearch, setLastSearch] = useHistoryState('proposalSearch', null);

  const [searchKey, setSearchKey] = useState(
    lastSearch?.searchKey ?? SEARCH_OPTIONS[0].value,
  );
  const [searchValue, setSearchValue] = useState(
    lastSearch?.searchValue ?? '',
  );
  const [searchResult, setSearchResult] = useState(null);
  const [bids, setBids] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(lastSearch?.offset ?? 0);
  const [limit, setLimit] = useState(lastSearch?.limit ?? 10);
  const [order, setOrder] = useState(lastSearch?.order ?? []);

  const genSearchResult = (cnt) => {
    const key = getKeyLabel(searchKey, true);
    return `${cnt ? 'Search' : 'No'} Results for ${key} "${searchValue}"`;
  };

  const [getProposals, { loading }] = useLazyQuery(GET_BIDS_FOR_EVALUATION, {
    onCompleted: ({ getBidsForEvaluation: res }) => {
      setCount(res.count);
      setBids(res.rows);
      setSearchResult(genSearchResult(res.count));
    },
  });

  const runSearch = (search) => {
    if (search.searchValue) {
      setLastSearch(search);
    }
    getProposals({
      variables: {
        criteria: { [search.searchKey]: search.searchValue },
        order: search.order,
        limit: search.limit,
        offset: search.offset,
      },
    });
  };

  useEffect(() => {
    if (lastSearch?.searchValue) {
      runSearch(lastSearch);
    }
  }, []);

  const onSearch = () => {
    if (searchValue) {
      setOffset(0); // reset to page 1
      runSearch({ searchKey, searchValue, order, limit, offset: 0 });
    }
  };

  const onPageChange = (currentPage, itemsPerPage) => {
    let newOffset;
    if (itemsPerPage === limit) {
      newOffset = (currentPage - 1) * itemsPerPage;
    } else {
      newOffset = 0; // reset to page 1
      setLimit(itemsPerPage);
    }
    setOffset(newOffset);
    runSearch({
      searchKey,
      searchValue,
      order,
      limit: itemsPerPage,
      offset: newOffset,
    });
  };

  const onSort = (val) => {
    const newOrder = parseSortValue(val);
    setOrder(newOrder);
    runSearch({ searchKey, searchValue, order: newOrder, limit, offset });
  };

  return (
    <div className="margin-bottom-8">
      <Breadcrumbs
        current="Proposal evaluation"
        path={[
          {
            location: `${window.AFP_CONFIG.appURLs?.home}/home`,
            label: 'Home',
          },
        ]}
      />
      <div className="grid-row margin-bottom-2">
        <div className="grid-col-10">
          <PageTitle
            title="Proposal evaluation"
            aria-label="Proposal evaluation"
            className="margin-bottom-0"
          />
          <div>
            Search by solicitation or vendor, to see a list of line items
            available for evaluation.
          </div>
        </div>
        <div className="grid-col-2 text-right padding-top-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/catalog/bid-reports')}
            label="Bid reports"
          />
        </div>
      </div>
      <BidSearchSection
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onSearch={onSearch}
        initialVendorName={
          lastSearch?.searchKey === 'vendorNameOrUei'
            ? lastSearch.searchValue
            : ''
        }
      />
      {searchResult && (
        <div className="text-bold margin-top-05">{searchResult}</div>
      )}
      <BidEvaluationTable
        loading={loading}
        data={bids}
        count={count}
        offset={offset}
        limit={limit}
        onPageChange={onPageChange}
        onSort={onSort}
      />
    </div>
  );
};

export default BidSearch;
